import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models
// ============================================================

export interface F913 {
  f913_01: string;   // 員工代號 (PK part 1, 8 chars)
  f913_02: string;   // 年度 (PK part 2, 4 chars)
  f913_11: number;   // 特休今年新增天數
  f913_111: number;  // 特休今年新增天數(備用)
  f913_12: number;   // 特休去年剩餘天數
  f913_121: number;  // 特休去年剩餘天數(備用)
  f913_21: number;   // 特休期初已用天數
  f913_211: number;  // 特休期初已用天數(備用)

  // Association
  details?: F9131[];
}

export interface F9131 {
  f9131_01: string;        // 員工代號 (FK)
  f9131_02: string;        // 年度 (FK)
  f9131_03: string;        // 假別代碼 (2 chars)
  f9131_11: number;        // 可用天數
  f9131_111: number;       // 可用天數(備用)
  f9131_12: string | null; // 可用開始日期
  f9131_13: number;        // 累計請假數
  f9131_131: number;       // 累計請假數(備用)
  f9131_14: number;        // 已用(核准數)
  f9131_141: number;       // 已用(核准數)(備用)
  f9131_91: string;        // 建立者ID
  f9131_92: string | null; // 建立日期
  f9131_93: string;        // 建立時間
}

// ============================================================
// Request / Response types
// ============================================================

export interface PW9213ListResponse {
  data: F913[];
  total: number;
  page: number;
  size: number;
}

export interface PW9213CreateRequest {
  master: Partial<F913>;
  details: Partial<F9131>[];
}

// ============================================================
// API methods
// ============================================================

export const pw9213Api = {
  async list(page = 1, size = 20): Promise<PW9213ListResponse> {
    const res = await apiClient.get('/api/pw9213', { params: { page, size } });
    return res.data;
  },

  async search(keyword: string): Promise<PW9213ListResponse> {
    const res = await apiClient.get('/api/pw9213/search', { params: { keyword } });
    return res.data;
  },

  /** Composite key: empCode + year */
  async get(empCode: string, year: string): Promise<F913> {
    const res = await apiClient.get(`/api/pw9213/${encodeURIComponent(empCode)}/${encodeURIComponent(year)}`);
    return res.data;
  },

  async create(data: PW9213CreateRequest): Promise<F913> {
    const res = await apiClient.post('/api/pw9213', data);
    return res.data;
  },

  /** Composite key: empCode + year */
  async update(empCode: string, year: string, data: PW9213CreateRequest): Promise<F913> {
    const res = await apiClient.put(`/api/pw9213/${encodeURIComponent(empCode)}/${encodeURIComponent(year)}`, data);
    return res.data;
  },

  /** Composite key: empCode + year */
  async delete(empCode: string, year: string): Promise<void> {
    await apiClient.delete(`/api/pw9213/${encodeURIComponent(empCode)}/${encodeURIComponent(year)}`);
  },

  /** Copy record: POST /api/pw9213/:empCode/:year/copy */
  async copy(empCode: string, year: string): Promise<F913> {
    const res = await apiClient.post(`/api/pw9213/${encodeURIComponent(empCode)}/${encodeURIComponent(year)}/copy`);
    return res.data;
  },

  /** Lookup: employee, department, leaveType */
  async lookupName(type: string, code: string): Promise<string> {
    if (!code || !code.trim()) return '';
    try {
      const res = await apiClient.get(`/api/pw9213/lookup/${encodeURIComponent(type)}/${encodeURIComponent(code.trim())}`);
      return res.data.name || '';
    } catch {
      return '';
    }
  },
};
